import { ButtonHTMLAttributes, PropsWithChildren } from 'react';
import { colors, r4, s16, s4, s8, timeFastMs } from '@ardoq/design-tokens';
import { InlineText, text2Bold } from '@ardoq/typography';
import { getFocusRingCSS } from '@ardoq/style-helpers';
import styled, { css } from 'styled-components';
import { BadgeDot, BadgeDotSize, Tag } from '@ardoq/status-ui';
import { Icon, ICON_SELECTOR, IconName } from '@ardoq/icons';
import { TabProps } from './types';
import { WithPopover } from '@ardoq/popovers';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popover2025';

type NativeButtonProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'onBlur' | 'type' | 'autoFocus' | 'onFocus' | 'id'
>;

export type TabButtonProps = NativeButtonProps & TabProps;

const BUTTON_VERTICAL_PADDING = '12px';

const buttonStyles = css`
  background: ${colors.surfaceDefault};
  color: ${colors.textSubtle};

  &::after {
    height: ${s4};
    background: ${colors.surfaceActionBrand};
  }

  &:hover {
    background: ${colors.surfaceHover};
    color: ${colors.textModerate};
  }

  &:active {
    background: ${colors.surfaceSelected};
    color: ${colors.textModerate};
  }
`;

const activeStyles = css`
  && {
    color: ${colors.textBrandLight};
  }
`;

const TabButtonStyles = styled.button<{
  $isActive: boolean;
  $full?: boolean;
}>`
  ${text2Bold};
  display: flex;
  gap: ${s8};
  flex-shrink: 0;
  align-items: center;

  ${({ $full }) =>
    $full &&
    css`
      flex: 1;
      justify-content: center;
    `}

  border: 0;
  cursor: pointer;
  padding: ${BUTTON_VERTICAL_PADDING} ${s16};
  border-radius: ${r4};

  position: relative;
  &:focus-visible {
    ${getFocusRingCSS(0)}
  }

  &:disabled {
    pointer-events: none;
    color: ${colors.textDisabled};
  }

  &::after {
    transition: transform ${timeFastMs} ease;
    left: 50%;
    transform: translateX(-50%) scaleX(0);
    width: calc(100% - 2px);
    bottom: 0;
    content: ' ';
    position: absolute;
    display: block;
    border-radius: ${r4};
  }

  ${buttonStyles}

  ${({ $isActive }) =>
    $isActive &&
    css`
      ${activeStyles}
      &::after {
        transform: translateX(-50%) scaleX(1);
      }
    `}
`;

type ChevronTabButtonStylesProps = {
  $isVisible: boolean;
  $direction: 'left' | 'right';
  onClick?: VoidFunction;
};

const chevronStyles = css`
  && {
    border-color: ${colors.borderSubtle};
  }
`;

const ChevronTabButtonStyles = styled(
  TabButtonStyles
)<ChevronTabButtonStylesProps>`
  z-index: 1;
  position: absolute;
  padding: ${BUTTON_VERTICAL_PADDING} 0;
  border-radius: 0;
  margin: ${s4} 0;
  transition: transform 0.2s;

  ${ICON_SELECTOR} {
    margin: 0 ${s4};
  }
  ${chevronStyles}

  ${({ $direction, $isVisible }) =>
    $direction === 'left'
      ? css`
          left: 0;
          border-right: 1px solid;
          transform: translateX(${$isVisible ? '0%' : '-100%'});
        `
      : css`
          right: 0;
          border-left: 1px solid;
          transform: translateX(${$isVisible ? '0%' : '100%'});
        `}
`;

type ChevronTabButtonProps = {
  isVisible: boolean;
  direction: 'left' | 'right';
  onClick?: VoidFunction;
};

export const ChevronTabButton = ({
  direction,
  isVisible,
  ...restProps
}: ChevronTabButtonProps) => {
  return (
    <ChevronTabButtonStyles
      $isActive={false}
      $direction={direction}
      disabled={!isVisible}
      $isVisible={isVisible}
      {...restProps}
    >
      {direction === 'left' && <Icon iconName={IconName.CHEVRON_LEFT} />}
      {direction === 'right' && <Icon iconName={IconName.CHEVRON_RIGHT} />}
    </ChevronTabButtonStyles>
  );
};

export const TabButton = ({
  isDisabled,
  isActive,
  iconName,
  rightIconName,
  rightIconPopoverMessage,
  label,
  tag,
  tagStatusType,
  tagPopoverContent,
  statusType,
  badgeTooltipText,
  clickId,
  tabId,
  full,
  ...restButtonProps
}: PropsWithChildren<TabButtonProps>) => {
  return (
    <TabButtonStyles
      $full={full}
      disabled={isDisabled}
      $isActive={isActive || false}
      data-click-id={clickId}
      data-tab-id={tabId}
      role="tab"
      aria-selected={isActive}
      {...restButtonProps}
    >
      {iconName && <Icon iconName={iconName} />}
      <InlineText>{label}</InlineText>
      {rightIconName && (
        <WithUncontrolledPopover
          content={
            rightIconPopoverMessage ? (
              <PlainTextPopover>{rightIconPopoverMessage}</PlainTextPopover>
            ) : null
          }
        >
          <Icon iconName={rightIconName} />
        </WithUncontrolledPopover>
      )}
      {tag && (
        <WithPopover content={tagPopoverContent}>
          <Tag label={`${tag}`} statusType={tagStatusType} />
        </WithPopover>
      )}
      {statusType && (
        <BadgeDot
          color={statusType}
          size={BadgeDotSize.SMALL}
          data-tooltip-text={badgeTooltipText}
        />
      )}
    </TabButtonStyles>
  );
};
